/**
 * Canvas 2D font strings.
 *
 * `ctx.font` takes a CSS shorthand string and reads nothing from the cascade —
 * no custom properties, no inherited family. So every canvas in the app spelled
 * its font out by hand, twenty-one times, as `'9px "JetBrains Mono", monospace'`
 * and variations. Retiring the second typeface meant touching all of them, which
 * is the argument for their living behind one function.
 *
 * The family here is a literal name, not `var(--rt-font)` — canvas cannot resolve
 * custom properties. `canvasFontFamily()` lets a caller pass the resolved family
 * (next/font emits a hashed name), with the literal as fallback.
 *
 * There is deliberately no tabular-figures helper. `ctx.fontFeatureSettings` is
 * barely implemented, and canvas labels here are each positioned at a computed
 * x rather than flowing in a column, so proportional digits cost nothing. If a
 * canvas ever does lay numbers out in a column, that is the point to revisit it.
 */
import { FONT_FAMILY, fontWeight, type FontWeightName } from './typography';

export type CanvasFontOptions = {
  size: number;
  weight?: FontWeightName;
};

/**
 * The family string to use on a canvas in this document.
 *
 * Pass the value of `--rt-font` (or any resolved family) when you have it —
 * `getComputedStyle(el).fontFamily` on any element in the tree is the reliable
 * way to get what next/font actually registered.
 */
export function canvasFontFamily(resolved?: string): string {
  const family = resolved?.trim();
  if (family) return family;
  return `"${FONT_FAMILY}", system-ui, sans-serif`;
}

/** A `ctx.font` shorthand. */
export function canvasFont({ size, weight = 'regular' }: CanvasFontOptions, family?: string): string {
  return `${fontWeight[weight]} ${size}px ${canvasFontFamily(family)}`;
}

/**
 * The canvas type scale — the DOM scale's counterpart for text painted into a
 * <canvas> (chart ticks, plan dimensions, readout chips).
 *
 * Canvas text used to be sized per call site, and the call sites drifted small:
 * chart ticks at 9px, axis units at 8px, while the DOM beside them sat at 13–15.
 * Four roles, by job — the same "one role, one style" rule the DOM scale keeps
 * (AGENTS.md §0):
 *
 *   tick   axis tick numbers — the one dense role, so the one role under the
 *          DOM floor of 12px (11px is the charting norm for ticks)
 *   axis   an axis's unit or title ("Hz", "dB") — a tick's size, set medium so
 *          it reads as the axis's name rather than another tick
 *   label  annotations inside a plot or on a drawing ("Schroeder", a
 *          dimension, a wall's name) — the DOM caption floor
 *   value  a figure the reader came for (a readout chip on the plan, a marked
 *          value on a chart) — the DOM body-sm size, semibold
 *
 * `web/tests/unit/canvasTypeScale.test.ts` holds every drawer to these roles.
 */
export const canvasType = {
  tick: { size: 11, weight: 'regular' },
  axis: { size: 11, weight: 'medium' },
  label: { size: 12, weight: 'medium' },
  value: { size: 13, weight: 'semibold' },
} as const satisfies Record<string, Required<CanvasFontOptions>>;

export type CanvasTypeRole = keyof typeof canvasType;

/** `canvasFont` for a role of the canvas type scale. */
export function canvasTypeFont(role: CanvasTypeRole, family?: string): string {
  return canvasFont(canvasType[role], family);
}
